import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Grid
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { authenticationService } from "src/services";
import { get, post } from 'src/services/httpClient.service';

interface PaymentData {
  amount: string;
  currency: string;
  paymentDate: string;
  paymentGateway: string;
  userId: number;
  userName: string;
  userEmail: string;
  userPhone: string;
  userPhoneCode: string;
  description: string;
}

interface Props {
  userId: number;
  customerId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  fetchUserDetails: () => void;
  handleClose: () => void;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const UserOnlineWallet: React.FC<Props> = ({
  userId,
  customerId,
  userName,
  userEmail,
  userPhone,
  fetchUserDetails,
  handleClose
}) => {
  const { vendorId, franchiseId } =
    authenticationService.getVendorFranchiseIds();

  const API_URL = process.env.REACT_APP_BASE_API_URL;

  const [paymentData, setPaymentData] = useState<PaymentData>({
    amount: '',
    currency: 'INR',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentGateway: '',
    userId: userId,
    userName: userName,
    userEmail: userEmail,
    userPhone: userPhone,
    userPhoneCode: '+91',
    description: ''
  });

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<'success' | 'error'>('success');

  /** 🔹 Fetch user email & phone */
  // const fetchSubscriberDetails = async () => {
  //   try {
  //     const res = await get(
  //       `${API_URL}/api/subscriber/${userId}`
  //     );

  //     setPaymentData(prev => ({
  //       ...prev,
  //       userEmail: res?.email || '',
  //       userPhone: res?.phone || ''
  //     }));
  //   } catch (err) {
  //     console.error('Failed to fetch user details', err);
  //   }
  // };

  // useEffect(() => {
  //   fetchSubscriberDetails();
  // }, [userId]);

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !paymentData.amount ||
      !paymentData.paymentGateway ||
      !paymentData.description
    ) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Please fill all required fields');
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...paymentData,
        vendorId,
        franchiseId,
        paymentType: "WALLET",
        amount: Number(paymentData.amount),
        paymentDate: new Date(paymentData.paymentDate).toISOString()
      };

      const response = await post(
        `${API_URL}/api/payments/makeOnlinePayment`,
        payload
      );

      if (response?.data?.paymentUrl) {
        window.open(response.data.paymentUrl, "_blank", "noopener,noreferrer");
        //handlePaymentClick(response?.data);
      }

      setSnackbarSeverity('success');
      setSnackbarMessage('Payment initiated successfully');
      setSnackbarOpen(true);

      fetchUserDetails();
      setTimeout(handleClose, 1500);
    } catch (err) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to initiate payment');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentClick = (paymentResponse) => {

  const paymentWindow = window.open(
    "",
    "RazorpayQR",
    "width=500,height=600,menubar=no,toolbar=no,location=no,status=no,scrollbars=no,resizable=no"
  );
  console.log("paymentResponse:::", paymentResponse)
  paymentWindow.document.write(`
      <html>
      <head><title>Scan QR</title></head>
      <body style="display:flex;justify-content:center;align-items:center;height:100vh;">
          <img src="${paymentResponse.qrImageUrl}" width="300"/>
      </body>
      </html>
  `);

  window.addEventListener("message", (event) => {
    if (event.data.status === "SUCCESS") {
      paymentWindow.close();
      window.location.reload();
    }
  });
};

//const paymentGateways = ['RazorPay', 'PhonePe', 'GPay', 'CashFree', 'PayTm', 'PayU', 'PayPal', 'CCAvenue', 'Stripe'];
  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, maxWidth: 900 }}>

        <Grid container spacing={2}>

          {/* User Info */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Customer ID"
              value={customerId}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Subscriber Name"
              value={userName}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Email"
              value={paymentData.userEmail}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>

          {/* Phone */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Phone"
              value={paymentData.userPhone}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          
          {/* Payment Date */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Payment Date"
              name="paymentDate"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={paymentData.paymentDate}
              onChange={handleChange}
            />
          </Grid>

          {/* Payment Gateway */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Payment Gateway</InputLabel>
              <Select
                name="paymentGateway"
                value={paymentData.paymentGateway}
                onChange={handleChange}
              >
                <MenuItem value="RazorPay">RazorPay</MenuItem>
                <MenuItem value="Stripe">Stripe</MenuItem>
                <MenuItem value="PayU">PayU</MenuItem>
                <MenuItem value="CCAvenue">CCAvenue</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Amount */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Amount"
              name="amount"
              type="number"
              fullWidth
              value={paymentData.amount}
              onChange={handleChange}
            />
          </Grid>

          {/* Currency */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Currency"
              value="INR"
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>

          


          {/* Description */}
          <Grid item xs={12}>
            <TextField
              label="Description"
              name="description"
              fullWidth
              value={paymentData.description}
              onChange={handleChange}
            />
          </Grid>

          {/* Buttons */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Pay Now'}
            </Button>

            <Button
              variant="outlined"
              sx={{ mt: 2, ml: 2 }}
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
          </Grid>

        </Grid>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserOnlineWallet;
